import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { SearchableDropdownField } from 'ui-components/components/dropdown';

import { messages } from '../../fulfillment-planner/messages';
import { ShippingStrategyDefaultTagWithName } from '../shipping-strategy-default-tag/shipping-strategy-default-tag-with-name';
import { isProviderServiceSelected } from './group-hooks/group-hooks-utils';

/**
 * @typedef {import('../../prop-types/intl-types').MessageDescriptor} MessageDescriptor
 */

/**
 * @typedef {object} ShippingService
 * @property {string} name
 * @property {string} [serviceId]
 * @property {string} [sellerLabelProviderId]
 * @property {string} [serviceCollectionGuid]
 * @property {boolean} [isTemplate]
 */

/**
 * @typedef {object} ShippingServiceGroup
 * @property {string} name
 * @property {ShippingService[]} items
 * @property {string} groupId
 * @property {string} [groupClassname]
 */

/**
 * @param {ShippingService} option
 * @returns {string}
 */
const getOptionLabel = (option) => option.name;

/**
 * @param {ShippingService} option
 * @param {ShippingService[]} selectedOptions
 * @returns {boolean|*}
 */
const getOptionIsSelected = (option, selectedOptions) => isProviderServiceSelected(option, selectedOptions[0]);

/**
 * @param {ShippingService} option
 * @returns {ReactNode}
 */
const renderOption = (option) => {
  const { name, isTemplate } = option;

  return !isTemplate ? name : <ShippingStrategyDefaultTagWithName name={name} />;
};

/**
 * @param {object} props
 * @param {string} [props.id]
 * @param {boolean} [props.isDisabled]
 * @param {(selectedService: ShippingService | object) => void} props.onSelected
 * @param {MessageDescriptor} [props.placeholderMessageDescriptor]
 * @param {(selectedService: ShippingService) => ReactNode} [props.renderSelectedValue]
 * @param {ShippingServiceGroup[]} props.serviceGroups
 * @param {ShippingService} [props.selectedOption]
 * @param {boolean} [props.shouldTruncateGroupHeader]
 * @returns {JSX.Element}
 */
export const ShippingServiceSearchableDropdown = ({
  id,
  isDisabled,
  onSelected,
  placeholderMessageDescriptor = messages.defaultSelectServicePlaceholder,
  renderSelectedValue,
  serviceGroups,
  selectedOption,
  shouldTruncateGroupHeader,
}) => {
  const intl = useIntl();

  const optionGroups = useMemo(() => {
    return serviceGroups.reduce((acc, serviceGroup) => {
      if (!serviceGroup?.items?.length) {
        return acc;
      }

      const { name, items = [], groupId, groupClassname } = serviceGroup;

      acc.push({
        label: typeof name === 'string' ? name : intl.formatMessage(name, name?.values),
        options: items.map((item) => ({
          ...item,
          groupId,
        })),
        groupHeaderClassName: groupClassname,
      });

      return acc;
    }, []);
  }, [serviceGroups, intl]);

  const onOptionSelected = useCallback(
    (_, newService) => {
      if (newService) {
        onSelected(newService);
      } else {
        onSelected({});
      }
    },
    [onSelected],
  );

  // Consumers can pass in an empty object or an object with null values to indicate no selection.
  // In order for the underlying dropdown to show its placeholder, we need to pass in null instead
  const _selectedOption = selectedOption?.serviceId || selectedOption?.serviceCollectionGuid ? selectedOption : null;

  return (
    <SearchableDropdownField
      ariaLabel={intl.formatMessage(messages.shippingService)}
      getOptionIsSelected={getOptionIsSelected}
      getOptionLabel={getOptionLabel}
      id={id}
      isClearable
      isDisabled={isDisabled}
      onItemSelected={onOptionSelected}
      optionGroups={optionGroups}
      placeholder={intl.formatMessage(placeholderMessageDescriptor)}
      renderOption={renderOption}
      renderSelectedValue={renderSelectedValue}
      selectedOption={_selectedOption}
      shouldTruncateGroupHeader={shouldTruncateGroupHeader}
    />
  );
};
